import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { proposals, exporter, getStoredUser, ingestion, getActiveProposalId, setActiveProposalId } from "@/lib/api";
import ComplianceTab from "@/components/ComplianceTab";
import SubmissionTab from "@/components/SubmissionTab";

const TABS = ["editor", "compliance", "submission", "team", "history", "audit"];

const DOC_PILL = { indexed: "pill-green", expanded: "pill-blue", empty: "pill-gray", pending: "pill-gray", failed: "pill-red" };

const dotColor = (s) => {
  if (s === "complete") return "var(--success)";
  if (s === "in_progress") return "var(--primary)";
  if (s === "partial") return "var(--warning)";
  return "var(--text-muted)";
};

const pctColor = (p) => {
  if (p >= 95) return "var(--success)";
  if (p >= 50) return "var(--primary)";
  if (p > 0) return "var(--warning)";
  return "var(--text-muted)";
};

export default function Workspace() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const proposalId = params.get("proposal");
  const initialTab = params.get("tab");

  const [proposal, setProposal] = useState(null);
  const [propList, setPropList] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [content, setContent] = useState("");
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [suggestion, setSuggestion] = useState(null);
  const [assistLoading, setAssistLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(TABS.includes(initialTab) ? initialTab : "editor");
  const saveTimer = useRef(null);
  const user = getStoredUser();

  // Collaboration state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [versions, setVersions] = useState([]);
  const [audit, setAudit] = useState([]);
  const [locking, setLocking] = useState(false);
  const [attaching, setAttaching] = useState(false);
  const docRef = useRef(null);
  const amendRef = useRef(null);
  const [ready, setReady] = useState(false);

  const load = async () => {
    try {
      const list = await proposals.list();
      // Most-recently-updated first, so the switcher and the no-param default
      // surface the proposal you were last working on (not an arbitrary one).
      const byRecent = [...list].sort((a, b) =>
        (b.updated_at || b.created_at || "").localeCompare(a.updated_at || a.created_at || "")
      );
      setPropList(byRecent);
      // Prefer the explicit ?proposal= id; otherwise pick up whatever proposal
      // Compliance/Submission last left active, so switching there and back
      // doesn't lose your place. Falls back to most-recent if that's gone
      // (deleted/archived/stale link) so the page never hangs on a missing id.
      const lastActiveId = getActiveProposalId();
      const target = (proposalId && list.find((p) => p.id === proposalId))
        || (lastActiveId && list.find((p) => p.id === lastActiveId))
        || byRecent[0];
      if (!target) { setProposal(null); return; }
      const full = await proposals.get(target.id);
      setProposal(full);
      setActiveProposalId(full.id);
      if (!activeId && full.sections?.length) setActiveId(full.sections[0].id);
    } catch (e) {
      setProposal(null);
    } finally {
      setReady(true);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proposalId]);

  useEffect(() => {
    if (!proposal || !activeId) return;
    const s = proposal.sections.find((x) => x.id === activeId);
    setContent(s?.content || "");
    setSuggestion(null);
  }, [activeId, proposal?.id]);

  const activeSection = useMemo(
    () => proposal?.sections.find((s) => s.id === activeId),
    [proposal, activeId]
  );

  const totalPct = useMemo(() => {
    if (!proposal) return 0;
    const arr = proposal.sections.map((s) => s.percent || 0);
    return Math.round(arr.reduce((a, b) => a + b, 0) / Math.max(1, arr.length));
  }, [proposal]);

  // Debounced save
  const handleChange = (v) => {
    setContent(v);
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      if (!proposal || !activeId) return;
      setSaving(true);
      try {
        const { section } = await proposals.updateSection(proposal.id, activeId, { content: v });
        setProposal((p) => ({
          ...p,
          sections: p.sections.map((s) => (s.id === section.id ? section : s)),
        }));
      } finally {
        setSaving(false);
      }
    }, 700);
  };

  const generate = async () => {
    if (!proposal || !activeId) return;
    setGenerating(true);
    setSuggestion(null);
    try {
      const result = await proposals.generate(proposal.id, activeId);
      setSuggestion(result);
    } catch (e) {
      setSuggestion({ error: e?.response?.data?.detail || "Generation failed" });
    } finally {
      setGenerating(false);
    }
  };

  const acceptSuggestion = () => {
    if (!suggestion?.draft) return;
    const newContent = (content ? content + "\n\n" : "") + suggestion.draft;
    handleChange(newContent);
    setSuggestion(null);
  };

  const runAssist = async (action) => {
    if (!content || !proposal) return;
    setAssistLoading(true);
    try {
      const { text } = await proposals.assist(proposal.id, content, action);
      handleChange(text);
    } finally {
      setAssistLoading(false);
    }
  };

  // ---- Collaboration: comments, versions, locks, source docs
  const loadComments = async () => {
    if (proposal) setComments(await proposals.comments(proposal.id));
  };
  const loadVersions = async () => {
    if (proposal) setVersions(await proposals.versions(proposal.id));
  };
  const loadAudit = async () => {
    if (proposal) setAudit(await proposals.audit(proposal.id));
  };
  useEffect(() => { loadComments(); /* eslint-disable-next-line */ }, [proposal?.id]);
  useEffect(() => {
    if (activeTab === "history") loadVersions();
    if (activeTab === "audit") loadAudit();
    // eslint-disable-next-line
  }, [activeTab, proposal?.id]);

  const lockSection = async () => {
    setLocking(true);
    try { await proposals.lockSection(proposal.id, activeId); await load(); }
    catch (e) { window.alert(e?.response?.data?.detail?.message || "Could not lock section"); }
    finally { setLocking(false); }
  };
  const unlockSection = async () => {
    setLocking(true);
    try { await proposals.unlockSection(proposal.id, activeId); await load(); }
    catch (e) { window.alert(e?.response?.data?.detail?.message || e?.response?.data?.detail || "Could not unlock"); }
    finally { setLocking(false); }
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    const mentions = (newComment.match(/@[\w.@-]+/g) || []).map((m) => m.slice(1));
    await proposals.addComment(proposal.id, { section_id: activeId, body: newComment, mentions });
    setNewComment("");
    await loadComments();
  };
  const resolveComment = async (cid, resolved) => {
    await proposals.resolveComment(proposal.id, cid, resolved);
    await loadComments();
  };

  const saveVersion = async () => {
    try {
      const res = await proposals.createVersion(proposal.id);
      if (activeTab === "history") await loadVersions();
      window.alert(`Snapshot saved as version ${res.version_number}.`);
    } catch (e) {
      window.alert("Could not save version");
    }
  };
  const restoreVersion = async (vid, n) => {
    if (!window.confirm(`Restore version ${n}? The current state is snapshotted first.`)) return;
    await proposals.restoreVersion(proposal.id, vid);
    await load();
    await loadVersions();
    if (activeSection) setContent(proposal.sections.find((s) => s.id === activeId)?.content || "");
  };

  const _pollJob = async (jobId) => {
    for (let i = 0; i < 90; i++) {
      const job = await ingestion.job(jobId);
      if (job.status === "done" || job.status === "error") return job;
      await new Promise((r) => setTimeout(r, 2000));
    }
    return null;
  };

  const attachDocs = async (files) => {
    if (!files?.length) return;
    setAttaching(true);
    try {
      const { job_id } = await proposals.addDocuments(proposal.id, files);
      const job = await _pollJob(job_id);
      await load();
      window.alert(job?.status === "done" ? `Ingested: ${job.summary}` : "Attach failed");
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Attach failed");
    } finally {
      setAttaching(false);
    }
  };

  const amendDocs = async (files) => {
    if (!files?.length) return;
    if (!window.confirm("Re-ingest this as an amendment and diff against the current requirements?")) return;
    setAttaching(true);
    try {
      const { job_id } = await proposals.uploadAmendment(proposal.id, files);
      const job = await _pollJob(job_id);
      await load();
      window.alert(job?.status === "done" ? `Amendment applied — ${job.summary}` : "Amendment failed");
    } catch (e) {
      window.alert(e?.response?.data?.detail || "Amendment failed");
    } finally {
      setAttaching(false);
    }
  };

  if (!proposal) {
    return (
      <div className="page" data-testid="page-workspace">
        {!ready ? (
          <div className="center-load">
            <span className="spinner" /> Loading workspace…
          </div>
        ) : (
          <div className="center-load" style={{ flexDirection: "column", gap: 14 }} data-testid="workspace-empty">
            <div>No responses to work on yet.</div>
            <button className="btn btn-primary" onClick={() => navigate("/new-response")}>+ New Response</button>
          </div>
        )}
      </div>
    );
  }

  const wordCount = (content || "").split(/\s+/).filter(Boolean).length;
  const daysLeft = proposal.due_date
    ? Math.max(0, Math.ceil((new Date(proposal.due_date) - new Date()) / (1000 * 60 * 60 * 24)))
    : null;

  const rawLock = activeSection?.lock;
  const lock = rawLock && rawLock.expires_at && new Date(rawLock.expires_at) > new Date() ? rawLock : null;
  const lockedByMe = lock && lock.owner_id === user?.id;
  const lockedByOther = lock && !lockedByMe;
  const sectionComments = comments.filter((c) => c.section_id === activeId);
  const documents = proposal.documents || [];
  const crossRefs = proposal.cross_references || [];

  return (
    <div className="page" data-testid="page-workspace">
      <div className="page-header">
        <div>
          <div className="page-title">Response Workspace</div>
          <div className="page-subtitle">
            {proposal.title} · {proposal.agency}
            {proposal.due_date ? ` · Due ${new Date(proposal.due_date).toDateString()}` : ""}
          </div>
        </div>
        <div className="flex gap-8 items-center">
          {propList.length > 1 && (
            <select
              className="select-filter"
              value={proposal.id}
              onChange={(e) => navigate(`/workspace?proposal=${e.target.value}`)}
              data-testid="proposal-switcher"
              title="Switch proposal"
              style={{ maxWidth: 240 }}
            >
              {propList.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.title}{(p.status && p.status !== "submitted") ? " (draft)" : ""}
                </option>
              ))}
            </select>
          )}
          <span className="pill pill-blue">{proposal.rfp_type}</span>
          {daysLeft != null && <span className="pill pill-orange">⏰ {daysLeft} days left</span>}
          <button className="btn btn-ghost btn-sm" disabled={saving} onClick={saveVersion} data-testid="save-draft-btn">
            {saving ? <><span className="spinner" /> Saving…</> : "💾 Snapshot Version"}
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => setActiveTab("submission")}
            data-testid="build-submission-btn"
          >
            📦 Build Submission
          </button>
        </div>
      </div>

      <div className="card" style={{ padding: "16px 20px" }}>
        <div className="flex items-center justify-between" style={{ marginBottom: 8 }}>
          <span className="font-700" style={{ fontSize: 13 }}>Overall Completion</span>
          <span className="font-700" style={{ color: "var(--primary)" }}>{totalPct}%</span>
        </div>
        <div className="progress-bar" style={{ height: 8 }}>
          <div className="progress-fill fill-blue" style={{ width: `${totalPct}%` }} />
        </div>
        <div className="flex gap-8 mt-8" style={{ fontSize: 11, color: "var(--text-secondary)" }}>
          <span>✅ {proposal.sections.filter((s) => s.percent >= 95).length} sections complete</span>
          <span>·</span>
          <span>✏️ {proposal.sections.filter((s) => s.percent > 0 && s.percent < 95).length} in progress</span>
          <span>·</span>
          <span>⭕ {proposal.sections.filter((s) => !s.percent).length} not started</span>
          <span>·</span>
          <span>🤖 {proposal.requirements.length} requirements tracked</span>
        </div>
      </div>

      <div className="tabs">
        <button className={`tab ${activeTab === "editor" ? "active" : ""}`} onClick={() => setActiveTab("editor")} data-testid="tab-editor">
          ✏️ AI Response Editor
        </button>
        <button className={`tab ${activeTab === "compliance" ? "active" : ""}`} onClick={() => setActiveTab("compliance")} data-testid="tab-compliance">
          ✅ Compliance Matrix
        </button>
        <button className={`tab ${activeTab === "submission" ? "active" : ""}`} onClick={() => setActiveTab("submission")} data-testid="tab-submission">
          📦 Submission Builder
        </button>
        <button className={`tab ${activeTab === "team" ? "active" : ""}`} onClick={() => setActiveTab("team")} data-testid="tab-team">
          👥 Team Assignments
        </button>
        <button className={`tab ${activeTab === "history" ? "active" : ""}`} onClick={() => setActiveTab("history")} data-testid="tab-history">
          🕒 Version History
        </button>
        <button className={`tab ${activeTab === "audit" ? "active" : ""}`} onClick={() => setActiveTab("audit")} data-testid="tab-audit">
          📋 Audit Log
        </button>
      </div>

      {activeTab === "editor" && (
      <div className="editor-layout">
        <div className="flex flex-col" style={{ gap: 12, minWidth: 0 }}>
        <div className="card" style={{ overflow: "hidden" }}>
          <div className="card-header" style={{ padding: "12px 16px" }}>
            <span className="card-title" style={{ fontSize: 12 }}>SECTIONS ({proposal.sections.length})</span>
            <span style={{ fontSize: 11, color: "var(--text-muted)" }}>{totalPct}% done</span>
          </div>
          <div style={{ overflowY: "auto", maxHeight: 420 }}>
            <div className="section-list">
              {proposal.sections.map((s) => {
                const sl = s.lock && s.lock.expires_at && new Date(s.lock.expires_at) > new Date();
                return (
                <button
                  key={s.id}
                  className={`section-item ${s.id === activeId ? "active" : ""}`}
                  onClick={() => setActiveId(s.id)}
                  data-testid={`section-${s.id}`}
                >
                  <div className="section-dot" style={{ background: dotColor(s.status) }} />
                  <span>{sl ? "🔒 " : ""}{s.title}</span>
                  <span className="section-pct" style={{ color: pctColor(s.percent || 0) }}>
                    {s.percent || 0}%
                  </span>
                </button>
                );
              })}
            </div>
          </div>
        </div>

        <div className="card" style={{ overflow: "hidden" }} data-testid="source-docs-card">
          <div className="card-header" style={{ padding: "12px 16px", flexWrap: "wrap", rowGap: 8 }}>
            <span className="card-title" style={{ fontSize: 12, flex: "1 1 100%" }}>SOURCE DOCUMENTS ({documents.length})</span>
            <div className="flex gap-8">
              <button className="btn btn-ghost btn-sm" onClick={() => docRef.current?.click()} disabled={attaching} data-testid="attach-doc-btn">
                {attaching ? <span className="spinner" /> : "＋ Add"}
              </button>
              <button className="btn btn-ghost btn-sm" onClick={() => amendRef.current?.click()} disabled={attaching} title="Re-ingest an amendment and diff vs the baseline" data-testid="amend-btn">
                ⚖ Amendment
              </button>
            </div>
            <input ref={docRef} type="file" multiple accept=".pdf,.docx,.doc,.txt,.xlsx,.msg,.zip" style={{ display: "none" }}
              onChange={(e) => { attachDocs(e.target.files); e.target.value = ""; }} data-testid="attach-doc-input" />
            <input ref={amendRef} type="file" multiple accept=".pdf,.docx,.txt,.xlsx,.zip" style={{ display: "none" }}
              onChange={(e) => { amendDocs(e.target.files); e.target.value = ""; }} data-testid="amend-input" />
          </div>
          <div style={{ padding: "8px 14px", maxHeight: 160, overflowY: "auto" }}>
            {documents.length === 0 && (
              <div style={{ fontSize: 11, color: "var(--text-muted)" }}>No source files attached yet.</div>
            )}
            {documents.map((d) => (
              <div key={d.id} className="flex items-center gap-8" style={{ fontSize: 11, padding: "2px 0" }}>
                <span className={`pill ${DOC_PILL[d.parse_status] || "pill-gray"}`} style={{ fontSize: 9 }}>{d.parse_status}</span>
                <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={d.filename}>{d.filename}</span>
              </div>
            ))}
            {crossRefs.length > 0 && (
              <div style={{ marginTop: 8, borderTop: "1px solid var(--border)", paddingTop: 6 }} data-testid="crossrefs">
                <div style={{ fontSize: 10, fontWeight: 700, color: "var(--text-muted)", marginBottom: 4 }}>CROSS-REFERENCES ({crossRefs.length})</div>
                {crossRefs.slice(0, 12).map((c, i) => (
                  <div key={i} className="flex items-center gap-8" style={{ fontSize: 11, padding: "1px 0" }} title={`${c.ref} → ${c.target}`}>
                    <span className="pill pill-blue" style={{ fontSize: 9 }}>{c.ref}</span>
                    <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: c.target_kind === "attachment" ? "var(--primary)" : "var(--text-muted)" }}>
                      {c.target_kind === "attachment" ? `↗ ${c.target}` : "in-document"}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
        </div>

        <div className="editor-area">
          <div className="editor-toolbar">
            <button
              className="toolbar-btn ai-btn"
              onClick={generate}
              disabled={generating}
              data-testid="generate-ai-btn"
            >
              {generating ? <><span className="spinner white" /> Generating…</> : "🤖 Generate with AI"}
            </button>
            <button
              className="toolbar-btn"
              onClick={() => runAssist("expand")}
              disabled={!content || assistLoading}
              data-testid="ai-expand-btn"
            >
              ⤢ Expand
            </button>
            <button
              className="toolbar-btn"
              onClick={() => runAssist("condense")}
              disabled={!content || assistLoading}
              data-testid="ai-condense-btn"
            >
              ⤡ Condense
            </button>
            <button
              className="toolbar-btn"
              onClick={() => runAssist("rewrite_clarity")}
              disabled={!content || assistLoading}
              data-testid="ai-rewrite-btn"
            >
              ✨ Rewrite
            </button>
            <button
              className="toolbar-btn"
              onClick={() => runAssist("make_more_technical")}
              disabled={!content || assistLoading}
              data-testid="ai-technical-btn"
            >
              🔬 Make Technical
            </button>
            <div className="toolbar-sep" />
            {lockedByOther ? (
              <span className="pill pill-orange" style={{ marginLeft: "auto" }} data-testid="lock-indicator">
                🔒 Locked by {lock.owner_name}
              </span>
            ) : lockedByMe ? (
              <button className="toolbar-btn" style={{ marginLeft: "auto" }} onClick={unlockSection} disabled={locking} data-testid="unlock-btn">
                🔓 Release Lock
              </button>
            ) : (
              <button className="toolbar-btn" style={{ marginLeft: "auto" }} onClick={lockSection} disabled={locking} data-testid="lock-btn">
                🔒 Lock Section
              </button>
            )}
          </div>
          <div className="editor-content" style={{ borderRadius: 0 }}>
            <p style={{ fontSize: 11, color: "var(--text-muted)", marginBottom: 12 }}>
              {activeSection?.title?.toUpperCase()} · Assigned to: {activeSection?.assignee || "Unassigned"} ·{" "}
              {lockedByOther ? `🔒 Locked by ${lock.owner_name}` : assistLoading ? "AI assisting…" : saving ? "Saving…" : "Ready"}
            </p>
            <textarea
              className="editor-textarea"
              value={content}
              onChange={(e) => handleChange(e.target.value)}
              disabled={lockedByOther}
              placeholder={lockedByOther ? "This section is locked by another contributor." : "Type your response or click 'Generate with AI' to draft this section using your knowledge base…"}
              data-testid="editor-textarea"
            />

            {suggestion?.error && (
              <div className="alert alert-danger" style={{ marginTop: 12 }} data-testid="ai-error">
                <span>⚠️</span>
                <span>{suggestion.error}</span>
              </div>
            )}

            {suggestion?.draft && (
              <div className="ai-suggestion" data-testid="ai-suggestion">
                <div className="ai-suggestion-header">
                  🤖 AI Suggestion — Knowledge Base Match ({suggestion.confidence}% confidence)
                </div>
                <div className="ai-suggestion-text">{suggestion.draft}</div>
                {suggestion.citations?.length > 0 && (
                  <div className="ai-citations">
                    <strong>Sources:</strong>
                    {suggestion.citations.map((c, i) => (
                      <span className="cite" key={i}>
                        • {c.kb_title} — "{c.passage_excerpt}"
                      </span>
                    ))}
                  </div>
                )}
                <div className="ai-actions">
                  <button className="btn btn-primary btn-sm" onClick={acceptSuggestion} data-testid="accept-suggestion-btn">
                    ✅ Accept
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={generate} disabled={generating}>
                    🔄 Regenerate
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => setSuggestion(null)}>
                    ❌ Dismiss
                  </button>
                </div>
              </div>
            )}
          </div>

          <div style={{ borderTop: "1px solid var(--border)", padding: "12px 16px" }} data-testid="comments-panel">
            <div className="font-700" style={{ fontSize: 12, marginBottom: 8 }}>
              💬 Comments {sectionComments.length > 0 && `(${sectionComments.length})`}
            </div>
            <div style={{ maxHeight: 180, overflowY: "auto", marginBottom: 8 }}>
              {sectionComments.length === 0 && (
                <div style={{ fontSize: 11, color: "var(--text-muted)" }}>No comments on this section yet.</div>
              )}
              {sectionComments.map((c) => (
                <div key={c.id} style={{ fontSize: 12, padding: "6px 0", borderBottom: "1px solid var(--border)", opacity: c.resolved ? 0.55 : 1 }}>
                  <div className="flex items-center justify-between">
                    <span className="font-700" style={{ fontSize: 11 }}>{c.author_email}</span>
                    <button
                      className="btn btn-ghost btn-sm"
                      style={{ padding: "0 6px", fontSize: 10 }}
                      onClick={() => resolveComment(c.id, !c.resolved)}
                      data-testid={`comment-resolve-${c.id}`}
                    >
                      {c.resolved ? "Reopen" : "Resolve"}
                    </button>
                  </div>
                  <div>{c.body}</div>
                </div>
              ))}
            </div>
            <div className="flex gap-8">
              <input
                style={{ flex: 1 }}
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addComment()}
                placeholder="Add a comment… use @name to mention"
                data-testid="comment-input"
              />
              <button className="btn btn-primary btn-sm" onClick={addComment} disabled={!newComment.trim()} data-testid="comment-add-btn">
                Post
              </button>
            </div>
          </div>

          <div className="editor-footer">
            <span>
              ✅ Compliance:{" "}
              {proposal.requirements.filter((r) =>
                (r.section || "").toLowerCase().includes((activeSection?.title || "").split(". ")[1]?.toLowerCase() || "—")
              ).length}{" "}
              requirements mapped to this section
            </span>
            <span>Words: {wordCount} {activeSection?.page_limit ? `/ ${activeSection.page_limit * 380} max` : ""}</span>
            <div className="flex gap-8">
              <button
                className="btn btn-ghost btn-sm"
                onClick={() => {
                  const idx = proposal.sections.findIndex((s) => s.id === activeId);
                  if (idx > 0) setActiveId(proposal.sections[idx - 1].id);
                }}
                data-testid="prev-section-btn"
              >
                ← Prev
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={() => {
                  const idx = proposal.sections.findIndex((s) => s.id === activeId);
                  if (idx < proposal.sections.length - 1) setActiveId(proposal.sections[idx + 1].id);
                }}
                data-testid="next-section-btn"
              >
                Next Section →
              </button>
            </div>
          </div>
        </div>
      </div>
      )}

      {activeTab === "compliance" && (
        <ComplianceTab proposal={proposal} onEditResponse={() => setActiveTab("editor")} />
      )}

      {activeTab === "submission" && (
        <SubmissionTab proposal={proposal} />
      )}

      {activeTab === "team" && (
        <div className="card" data-testid="team-panel">
          <div className="card-header"><span className="card-title">Team Assignments</span></div>
          <table className="data-table">
            <thead>
              <tr><th>Section</th><th>Assignee</th><th>Status</th><th>Progress</th><th>Lock</th></tr>
            </thead>
            <tbody>
              {proposal.sections.map((s) => {
                const sl = s.lock && s.lock.expires_at && new Date(s.lock.expires_at) > new Date() ? s.lock : null;
                return (
                  <tr key={s.id}>
                    <td>{s.title}</td>
                    <td>{s.assignee || <span style={{ color: "var(--text-muted)" }}>Unassigned</span>}</td>
                    <td><span className="pill" style={{ background: "transparent", color: dotColor(s.status) }}>{s.status}</span></td>
                    <td>{s.percent || 0}%</td>
                    <td>{sl ? `🔒 ${sl.owner_name}` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {activeTab === "history" && (
        <div className="card" data-testid="history-panel">
          <div className="card-header">
            <span className="card-title">Version History ({versions.length})</span>
            <button className="btn btn-primary btn-sm" onClick={saveVersion} data-testid="snapshot-version-btn">＋ Snapshot now</button>
          </div>
          <div style={{ padding: "8px 20px" }}>
            {versions.length === 0 && (
              <div style={{ fontSize: 12, color: "var(--text-muted)", padding: "12px 0" }}>
                No versions yet. Use “Snapshot now” to capture the current draft.
              </div>
            )}
            {versions.map((v) => (
              <div key={v.id} className="flex items-center justify-between" style={{ padding: "10px 0", borderBottom: "1px solid var(--border)" }}>
                <div>
                  <span className="font-700">v{v.version_number}</span>
                  <span className="pill pill-gray" style={{ marginLeft: 8, fontSize: 10 }}>{v.snapshot_type}</span>
                  <span style={{ fontSize: 11, color: "var(--text-muted)", marginLeft: 8 }}>
                    {new Date(v.created_at).toLocaleString()} · {v.created_by_email || "—"}
                  </span>
                </div>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => restoreVersion(v.id, v.version_number)}
                  data-testid={`restore-${v.id}`}
                >
                  ↩ Restore
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeTab === "audit" && (
        <div className="card" data-testid="audit-panel">
          <div className="card-header">
            <span className="card-title">Audit Log ({audit.length})</span>
            <button
              className="btn btn-outline btn-sm"
              onClick={() => exporter.auditCsv(proposal.id)}
              disabled={audit.length === 0}
              data-testid="audit-csv-btn"
            >
              ⬇ Export CSV
            </button>
          </div>
          <table className="data-table">
            <thead>
              <tr><th>When</th><th>Actor</th><th>Action</th><th>Resource</th></tr>
            </thead>
            <tbody>
              {audit.length === 0 && (
                <tr><td colSpan={4} style={{ color: "var(--text-muted)" }}>No audit entries yet.</td></tr>
              )}
              {audit.map((e) => (
                <tr key={e.id}>
                  <td style={{ whiteSpace: "nowrap" }}>{new Date(e.ts).toLocaleString()}</td>
                  <td>{e.actor_email || "—"}</td>
                  <td><span className="pill pill-gray" style={{ fontSize: 10 }}>{e.action}</span></td>
                  <td style={{ fontFamily: "monospace", fontSize: 11 }}>{e.resource}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
